// ============================================================
// ClipBrowser.jsx — the client's real uploaded video library (v1)
// ------------------------------------------------------------
// Lists clips for the client via /api/video/clips, lets them upload a new source
// video, and hands the picked clip to the editor via onPick. Uploads POST to the
// backend, which stores the file in the 'video-clips' bucket and inserts the
// video_clips row we render here.
//
// Phase 7: every read and write here used to run in the browser under RLS,
// scoped to the SIGNED-IN user. `clientId` comes from useClientId() by way of
// VideoEditorPanel, so it is the admin — an admin browsing a client's editor
// saw and edited their OWN clip library. Scope comes from the routes now; the
// prop stays, and every !clientId check below is a "workspace loaded yet" guard,
// not a scoping one.
//
// The onPick contract is unchanged: it receives a clip object the editor can
// load (id, url, filename, label, duration). Duration is measured from the
// <video> element later, so 0 here is fine.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authHeader, clientScopedFetch, withViewingAsClient } from '../../lib/authedFetch';
import { useViewingAs } from '../../hooks/useViewingAs';
import { fmtTime } from '../../utils/mockVideoAssets';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const MAX_BYTES = 500 * 1024 * 1024; // 500MB
const ACCEPT = '.mp4,.mov,.webm,video/mp4,video/quicktime,video/webm';
const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

// Strip the extension for a friendlier display label.
const clipLabel = (row) => (row.filename || 'Untitled clip').replace(/\.[^.]+$/, '');

// Map a video_clips DB row to the shape the editor (useVideoEditor.loadClip) wants.
const toEditorClip = (row) => ({
  id: row.id,
  url: row.url,
  filename: row.filename || null,
  storagePath: row.storage_path || null,
  label: clipLabel(row),
  duration: row.duration || 0, // measured from the <video> in the canvas
});

export default function ClipBrowser({ onPick, clientId, onBack }) {
  const [clips, setClips]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);   // per-file upload progress (0–100)
  const [batchTotal, setBatchTotal] = useState(0); // number of files in the current batch
  const [batchIndex, setBatchIndex] = useState(0); // 1-based index of the file uploading now
  const [queued, setQueued]     = useState(false); // true while waiting to retry a 429 (server busy)
  const [dragIndex, setDragIndex] = useState(null); // tile being dragged
  const [overIndex, setOverIndex] = useState(null); // tile dragged over
  const [libraryOnly, setLibraryOnly] = useState(false); // skip auto-opening the editor after upload
  const [renamingId, setRenamingId]   = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const fileInputRef = useRef(null);
  // clientId is the signed-in admin and does not change when an admin switches
  // which client they are viewing, so the load effect keys on this too.
  const { viewingAsClientId } = useViewingAs();

  // ── Load the client's library on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        // The project_id IS NULL filter (library clips only, excluding
        // per-project copies) and both orderings moved into the route.
        const res = await clientScopedFetch(`${BACKEND}/api/video/clips`);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setClips(body.clips || []);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load your clips');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  // ── Upload ──────────────────────────────────────────────────────────────────
  // The upload mechanism is unchanged (XHR so we get real progress events); it's
  // just wrapped in a Promise so the batch runner below can await one file at a
  // time. Resolves with the inserted video_clips row, or rejects with an Error
  // whose message is user-facing. Reports this file's progress via setProgress.
  const uploadOne = useCallback((file) => new Promise((resolve, reject) => {
    const typeOk =
      /\.(mp4|mov|webm)$/i.test(file.name) || ALLOWED_TYPES.includes(file.type);
    if (!typeOk) { reject(new Error('Please choose an MP4, MOV, or WEBM video.')); return; }
    if (file.size > MAX_BYTES) { reject(new Error('That file is over the 500MB limit.')); return; }
    if (!clientId) { reject(new Error('Still loading your workspace — try again in a moment.')); return; }

    const form = new FormData();
    form.append('file', file);
    // client_id REMOVED from the form, and its removal is load-bearing.
    // requireOwnerOrAdmin reads a body-borne client id BEFORE the query string,
    // and the backend's resolveMultipartClient let an admin's form field beat
    // req.clientId outright. This field carried the SIGNED-IN admin's id, so
    // leaving it while adding ?client= below would have kept every upload
    // landing in the admin's own library — with a 200 and no warning anywhere.
    // The route stopped consulting the form body in the same change.

    setProgress(0);

    // XHR (not fetch) so we get real upload-progress events — which is also
    // why this can't go through authedFetch. The same bearer token goes on by
    // hand instead, set after open() and before send() as the XHR API requires.
    //
    // And, for the same reason, the viewing-as override goes on by hand too:
    // clientScopedFetch does its work by rewriting the URL, so the rewrite is
    // applied directly here. This is the only caller in the app that needs
    // withViewingAsClient without the fetch wrapper around it.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', withViewingAsClient(`${BACKEND}/api/video/upload-clip`));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let row = null;
        try { row = JSON.parse(xhr.responseText); } catch { /* fallthrough */ }
        if (!row || !row.id) {
          reject(new Error('Upload finished but the server returned an unexpected response.'));
          return;
        }
        resolve(row);
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try { msg = JSON.parse(xhr.responseText).error || msg; } catch { /* keep default */ }
        const err = new Error(msg);
        err.status = xhr.status; // let runUploads retry on 429 (single-flight gate is busy)
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload — please try again.'));
    authHeader().then((auth) => {
      if (auth.Authorization) xhr.setRequestHeader('Authorization', auth.Authorization);
      xhr.send(form);
    });
  }), [clientId]);

  // Upload the selected files as a sequential queue (one at a time, never in
  // parallel). Each success is added to the library the moment it lands, and the
  // first successful clip is auto-selected in the editor once the whole batch is
  // done. With a single file this behaves exactly like the old handleFile.
  const runUploads = useCallback(async (files) => {
    if (!files.length) return;
    setError(null);
    setUploading(true);
    setBatchTotal(files.length);

    let firstRow = null;
    const failures = [];

    // The backend allows one clip upload at a time and returns 429 when busy.
    // Rather than surface that as an error, wait a beat and retry the same file
    // a few times so a batch drains itself as each upload finishes server-side.
    const MAX_429_RETRIES = 9;      // ~1.5 min total at 10s apart
    const RETRY_DELAY_MS  = 10000;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    for (let i = 0; i < files.length; i++) {
      setBatchIndex(i + 1);
      setProgress(0);

      let row = null;
      let lastErr = null;
      for (let attempt = 0; attempt <= MAX_429_RETRIES; attempt++) {
        try {
          row = await uploadOne(files[i]);
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          // Only 429 (server busy) is retryable; real failures fall through.
          if (err.status === 429 && attempt < MAX_429_RETRIES) {
            setQueued(true);
            await sleep(RETRY_DELAY_MS);
            setQueued(false);
            continue;
          }
          break;
        }
      }
      setQueued(false);

      if (row) {
        setClips((prev) => [row, ...prev]); // add to library as each finishes
        if (!firstRow) firstRow = row;
      } else if (lastErr) {
        failures.push({ name: files[i].name, message: lastErr.message });
      }
    }

    setUploading(false);
    setQueued(false);
    setProgress(0);
    setBatchTotal(0);
    setBatchIndex(0);

    if (failures.length) {
      if (files.length === 1) {
        setError(failures[0].message); // single-file: exact original error text
      } else {
        const detail = failures.map((f) => `${f.name}: ${f.message}`).join('; ');
        setError(`${failures.length} of ${files.length} uploads failed — ${detail}`);
      }
    }

    // "Just add to library" skips the auto-open — the file lands in the grid
    // above and the client can open it from there whenever they're ready.
    if (firstRow && !libraryOnly) onPick(toEditorClip(firstRow));
  }, [uploadOne, onPick, libraryOnly]);

  const onInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file(s) be re-selected later
    runUploads(files);
  };

  const openPicker = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  // ── Rename clip ───────────────────────────────────────────────────────────────
  // Renames the display label only (video_clips.filename); the underlying
  // storage object and URL are untouched. Preserves the original extension.
  const startRename = useCallback((clip, e) => {
    e.stopPropagation();
    setRenamingId(clip.id);
    setRenameValue(clipLabel(clip));
  }, []);

  const cancelRename = useCallback((e) => {
    e?.stopPropagation();
    setRenamingId(null);
  }, []);

  const commitRename = useCallback(async (clip) => {
    const nextLabel = renameValue.trim();
    setRenamingId(null);
    if (!nextLabel || nextLabel === clipLabel(clip)) return;
    const ext = (clip.filename || '').match(/\.[^.]+$/)?.[0] || '';
    const nextFilename = `${nextLabel}${ext}`;
    setClips((prev) => prev.map((c) => (c.id === clip.id ? { ...c, filename: nextFilename } : c)));
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/clips/${encodeURIComponent(clip.id)}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ patch: { filename: nextFilename } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      setError(`Could not rename clip: ${err?.message || err}`);
    }
  }, [renameValue]);

  // ── Delete clip ──────────────────────────────────────────────────────────────
  const handleDeleteClip = useCallback(async (clip, e) => {
    e.stopPropagation();
    setClips((prev) => prev.filter((c) => c.id !== clip.id));
    // The storage remove and the row delete are ONE server-side unit now. They
    // used to be two independent browser calls with the remove's rejection
    // swallowed by .catch(() => {}), which could leave the object behind with
    // nothing recording that it had happened.
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/clips/${encodeURIComponent(clip.id)}`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      setError(`Could not delete clip: ${err?.message || err}`);
      setClips((prev) => [clip, ...prev]);
    }
  }, []);

  // ── Drag-to-reorder ───────────────────────────────────────────────────────────
  // Persist the new order by writing each clip's array index as its sort_order.
  // On failure we revert and surface it.
  const persistOrder = useCallback(async (ordered, previous) => {
    // The one fan in Phase 7 that is the Calendar/PhotoLibrary class: N rows,
    // each taking a different sort_order. Phase 5's resolution applies
    // unchanged — one batch endpoint, non-atomic per-row loop server-side. The
    // route reports per-row outcomes so the revert below is unchanged.
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/video/clips/sort-batch`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ updates: ordered.map((c, i) => ({ id: c.id, sort_order: i })) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      if (!body.ok) {
        const first = (body.results || []).find((r) => !r.ok);
        throw new Error(first?.error || `${body.failedCount} row(s) failed`);
      }
    } catch (err) {
      setClips(previous); // revert optimistic reorder
      setError(`Couldn't save the new order: ${err?.message || err}`);
    }
  }, []);

  const handleDrop = useCallback((targetIndex) => {
    setClips((prev) => {
      if (dragIndex == null || dragIndex === targetIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(targetIndex, 0, moved);
      persistOrder(next, prev);
      return next;
    });
    setDragIndex(null);
    setOverIndex(null);
  }, [dragIndex, persistOrder]);

  // ── Render ──────────────────────────────────────────────────────────────────
  // "Uploading 2 of 5… 40%" for a batch; plain "Uploading… 40%" for a single file
  // so a one-file upload looks exactly as it did before.
  const uploadingLabel =
    queued
      ? 'Loading…'
      : batchTotal > 1
        ? `Uploading ${batchIndex} of ${batchTotal}… ${progress}%`
        : `Uploading… ${progress}%`;

  return (
    <div className="ve-browser">
      <div className="ve-browser-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onBack && (
            <button className="ve-back-btn" onClick={onBack} title="Back to projects">← Projects</button>
          )}
          <div className="ve-browser-title">Choose a clip to edit</div>
        </div>
        <div className="ve-browser-actions">
          <label className="ve-library-only-toggle" title="Upload without opening the editor">
            <input
              type="checkbox"
              checked={libraryOnly}
              onChange={(e) => setLibraryOnly(e.target.checked)}
              disabled={uploading}
            />
            Just add to library
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={onInputChange}
            style={{ display: 'none' }}
          />
          <button
            className="ve-btn ve-btn-primary ve-btn-sm"
            onClick={openPicker}
            disabled={uploading || !clientId}
          >
            {uploading ? uploadingLabel : '＋ Upload video'}
          </button>
        </div>
      </div>

      {uploading && (
        <div className="ve-upload-bar" aria-label={uploadingLabel}>
          <div className="ve-upload-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      {error && <div className="ve-browser-error">{error}</div>}

      {loading ? (
        <div className="ve-browser-empty">
          <div className="ve-empty-sub">Loading your video library…</div>
        </div>
      ) : clips.length === 0 ? (
        <div className="ve-browser-empty">
          <div className="ve-empty-icon" aria-hidden="true">🎬</div>
          <div className="ve-empty-title">Upload your first video</div>
          <div className="ve-empty-sub">
            Add an MP4, MOV, or WEBM clip (up to 500MB) to start editing.
          </div>
          <button
            className="ve-btn ve-btn-primary ve-btn-sm"
            onClick={openPicker}
            disabled={uploading || !clientId}
            style={{ marginTop: 14 }}
          >
            {uploading ? uploadingLabel : '＋ Upload video'}
          </button>
        </div>
      ) : (
        <>
          {clips.length > 1 && (
            <p className="ve-hint" style={{ margin: '0 0 10px' }}>
              Drag clips to reorder them.
            </p>
          )}
          <div className="ve-clip-grid">
            {clips.map((clip, i) => (
              <div
                key={clip.id}
                className={
                  've-clip-tile' +
                  (dragIndex === i ? ' dragging' : '') +
                  (overIndex === i && dragIndex !== i ? ' drag-over' : '')
                }
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => { e.preventDefault(); if (overIndex !== i) setOverIndex(i); }}
                onDrop={(e) => { e.preventDefault(); handleDrop(i); }}
                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                onClick={() => onPick(toEditorClip(clip))}
              >
                <div className="ve-clip-thumb">
                  <video
                    src={clip.url}
                    preload="metadata"
                    muted
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {clip.duration ? (
                    <span className="ve-clip-dur">{fmtTime(clip.duration)}</span>
                  ) : null}
                  <button
                    className="ve-clip-del"
                    onClick={(e) => handleDeleteClip(clip, e)}
                    title="Delete clip"
                  >
                    ✕
                  </button>
                  <div className="ve-clip-hover">
                    <span className="ve-btn ve-btn-primary ve-btn-sm">Edit</span>
                  </div>
                </div>
                {renamingId === clip.id ? (
                  <input
                    className="ve-rename-input ve-clip-rename-input"
                    value={renameValue}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(clip)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(clip); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelRename(e); }
                    }}
                  />
                ) : (
                  <div className="ve-clip-name-row">
                    <span className="ve-clip-name">{clipLabel(clip)}</span>
                    <button
                      className="ve-icon-btn"
                      onClick={(e) => startRename(clip, e)}
                      title="Rename clip"
                    >✎</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
